import {
  MorphemeGlyphTileUi,
  type MorphemeGlyphTileUiProps,
} from "#morpheme-glyph-tile-ui";
import type { GraphemeGlyph } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.entities";
import type { MorphemeGlyphTileGraphemeMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { useMorphemeGlyphTileGraphemeMorphemeSlice } from "#use-morpheme-glyph-tile-grapheme-morpheme-slice";
import { useMorphemeListStagesQuery } from "@-/dev.improve.haniverse.lib.morpheme.stage";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";

export type GraphemeMorphemeGlyphTileUiProps = {
  grapheme: string;
  graphemeGlyph?: GraphemeGlyph;
  /**
   * Pre-resolved grapheme slice (pinyin readings) from a batched parent query. When supplied
   * together with a `stage`, this tile fires NO per-tile queries — see {@link GraphemeMorphemeGlyphTileUi}.
   */
  morphemeGlyphTileSlice?: MorphemeGlyphTileGraphemeMorphemeSlice;
  primaryPinyin?: string;
} & Omit<MorphemeGlyphTileUiProps, "morphemeGlyphTileSlice" | "graphemeGlyph">;

/**
 * Grapheme tile whose SRS `stage` and pinyin slice are resolved by a BATCHED parent query and passed
 * in — it calls no data hooks. A grid that mounts hundreds of tiles at once renders these instead of
 * the self-resolving variant, so it doesn't fire a per-tile stage + pinyin query (the JS-thread storm
 * that serialized the whole page's queries behind it). Used when the caller supplies both `stage` and
 * `morphemeGlyphTileSlice`.
 */
function ResolvedGraphemeMorphemeGlyphTileUi({
  graphemeGlyph,
  morphemeGlyphTileSlice,
  ...morphemeGlyphTileProps
}: Omit<GraphemeMorphemeGlyphTileUiProps, "grapheme" | "primaryPinyin"> & {
  morphemeGlyphTileSlice: MorphemeGlyphTileGraphemeMorphemeSlice;
}) {
  return (
    <MorphemeGlyphTileUi
      graphemeGlyph={graphemeGlyph}
      morphemeGlyphTileSlice={morphemeGlyphTileSlice}
      {...morphemeGlyphTileProps}
    />
  );
}

/**
 * Grapheme tile that resolves its own SRS `stage` and pinyin slice via per-grapheme queries. The
 * fallback for callers that don't batch (single tiles, small grids). A large grid should resolve
 * both once at the parent and pass them in so {@link ResolvedGraphemeMorphemeGlyphTileUi} is used.
 */
function SelfResolvingGraphemeMorphemeGlyphTileUi({
  grapheme,
  graphemeGlyph,
  primaryPinyin,
  stage,
  ...morphemeGlyphTileProps
}: Omit<GraphemeMorphemeGlyphTileUiProps, "morphemeGlyphTileSlice">) {
  const morphemeGlyphTileSlice = useMorphemeGlyphTileGraphemeMorphemeSlice(
    grapheme,
    primaryPinyin,
  );

  // Single-grapheme tiles route through the batched list query with an array of one, so they share
  // the same stage read + change subscription as grids (there is no per-grapheme query variant).
  const { data: stagesByGrapheme } = useQuery(useMorphemeListStagesQuery([grapheme]));
  const resolvedStage = stage ?? stagesByGrapheme?.[grapheme];

  return (
    <MorphemeGlyphTileUi
      graphemeGlyph={graphemeGlyph}
      morphemeGlyphTileSlice={morphemeGlyphTileSlice}
      stage={resolvedStage}
      {...morphemeGlyphTileProps}
    />
  );
}

/**
 * A grapheme glyph tile. Its `stage` (SRS) and pinyin slice can either be resolved for it (by
 * a batched parent query — the fast path for grids of many tiles) or resolved by the tile itself.
 *
 * When BOTH `stage` and `morphemeGlyphTileSlice` are supplied, the tile renders with no data
 * hooks; otherwise it self-resolves both per grapheme. The choice is made once here by prop-presence
 * — a caller doesn't branch per tile.
 */
export function GraphemeMorphemeGlyphTileUi({
  morphemeGlyphTileSlice,
  stage,
  ...props
}: GraphemeMorphemeGlyphTileUiProps) {
  if (morphemeGlyphTileSlice !== undefined && stage !== undefined) {
    return (
      <ResolvedGraphemeMorphemeGlyphTileUi
        morphemeGlyphTileSlice={morphemeGlyphTileSlice}
        stage={stage}
        {...props}
      />
    );
  }

  return <SelfResolvingGraphemeMorphemeGlyphTileUi stage={stage} {...props} />;
}
